import React from 'react';
import {lazy, Suspense} from 'react';
import {Outlet, Navigate} from 'react-router-dom';
// Guards
import {AuthGuard, RoleBasedGuard, FeatureGuard, OnboardingGuard} from 'packages/common/guards';
// Layouts
import DashboardLayout from 'packages/common/layouts/app';
// Components
import {LoadingScreen} from 'packages/common/components/loading-screen';
// Roles & Paths
import {paths} from 'packages/common/routes/paths';
import {roles, routeRoles} from 'packages/common/utils/rolesConfig';
// Context
import {AuthProvider} from 'packages/common/contexts/auth-context';
import {UserDetailsProvider} from 'packages/common/contexts/user-details-context';

// Lazy-loaded Pages
// Global Pages
const OnboardingPage = lazy(() => import('apps/dashboard/pages/app/onboarding/view/OnboardingPage'));
const DashboardPage = lazy(() => import('apps/dashboard/pages/app/dashboard/view/DashboardPage'));
const CompanyPage = lazy(() => import('apps/dashboard/pages/app/company/view/CompanyPage'));
const SettingsPage = lazy(() => import('apps/dashboard/pages/app/settings/view/SettingsPage'));
const UserAccountPage = lazy(() => import('apps/dashboard/pages/app/account/view/UserAccountPage'));
const WidgetsPage = lazy(() => import('apps/dashboard/pages/app/widgets/view/WidgetsPage'));
const ReportingPage = lazy(() => import('apps/dashboard/pages/app/reporting/view/ReportingPage'));
// Giftvouchers
const GiftVoucherCatalog = lazy(() => import('apps/dashboard/pages/app/gift-voucher/catalog/views/CatalogPage'));
const GiftVouchersTypeCreate = lazy(() => import('apps/dashboard/pages/app/gift-voucher/catalog/views/CatalogCreatePage'));
const GiftVouchersTypeEdit = lazy(() => import('apps/dashboard/pages/app/gift-voucher/catalog/views/CatalogEditPage'));
const GiftVouchersManage = lazy(() => import('apps/dashboard/pages/app/gift-voucher/manage/view/ManagePage'));
const GiftVouchersDetails = lazy(() => import('apps/dashboard/pages/app/gift-voucher/details/view/DetailsPage'));
const GiftVouchersSearch = lazy(() => import('apps/dashboard/pages/app/gift-voucher/search/view/SearchPage'));
const GiftVouchersTransactions = lazy(() => import('apps/dashboard/pages/app/gift-voucher/transactions/view/TransactionsPage'));
// Customers
const CustomersListPage = lazy(() => import('apps/dashboard/pages/app/customers/view/CustomersListPage'));
// Employees
const EmployeesListPage = lazy(() => import('apps/dashboard/pages/app/employees/view/EmployeesListPage'));
// Wellness
const LandingPage = lazy(() => import('apps/dashboard/pages/app/wellness/landing/view/LandingPage'));
const CatalogPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/view/CatalogPage'));

const SpacesListPage = lazy(() => import('apps/dashboard/pages/app/wellness/spaces/view/SpacesListPage'));
const ServiceCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/services/view/ServiceCreatePage'));
const ServiceEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/services/view/ServiceEditPage'));
const ExtraCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/extras/view/ExtraCreatePage'));
const ExtraEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/extras/view/ExtraEditPage'));
const ProductCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/products/view/ProductCreatePage'));
const ProductEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/products/view/ProductEditPage'));
const CalendarPage = lazy(() => import('apps/dashboard/pages/app/wellness/calendar/view/CalendarPage'));
const WellnessBookingsPage = lazy(() => import('apps/dashboard/pages/app/wellness/booking/view/BookingsPage'));
const WellnessConfigPage = lazy(() => import('apps/dashboard/pages/app/wellness/config/view/WellnessConfigPage'));
const WellnessBookingCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/booking/view/BookingCreatePage'));
const WellnessBookingEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/booking/view/BookingEditPage'));
const WellnessPOSPage = lazy(() => import('apps/dashboard/pages/app/wellness/pos/view/POSPage'));
const WellnessPOSPaymentPage = lazy(() => import('apps/dashboard/pages/app/wellness/pos/view/POSPaymentPage'));
// Restaurant
const RestaurantSeatingPage = lazy(() => import('apps/dashboard/pages/app/restaurants/seating/view/SeatingPage'));
const RestaurantServicesPage = lazy(() => import('apps/dashboard/pages/app/restaurants/services/view/ServicesPage'));
const RestaurantReservationsPage = lazy(() => import('apps/dashboard/pages/app/restaurants/reservations/view/ReservationsPage'));

export const appRoutes = [
  {
    path: '/dashboard',
    element: (
      <AuthProvider>
        <UserDetailsProvider>
          <AuthGuard>
            <RoleBasedGuard roles={routeRoles[paths.app.root]}>
              <OnboardingGuard requireOnboardingCompleted={true}>
                <DashboardLayout>
                  <Suspense fallback={<LoadingScreen />}>
                    <Outlet />
                  </Suspense>
                </DashboardLayout>
              </OnboardingGuard>
            </RoleBasedGuard>
          </AuthGuard>
        </UserDetailsProvider>
      </AuthProvider>
    ),
    children: [
      // Routes publiques (pas besoin de guards supplémentaires)
      {
        path: 'onboarding',
        element: <OnboardingGuard requireOnboardingCompleted={false}><OnboardingPage /></OnboardingGuard>,
      },
      {
        path: '',
        element: <DashboardPage />,
        index: true,
      },
      {
        path: 'user-account',
        element: <UserAccountPage />,
      },

      // Routes COMPANY_ADMIN
      {
        element: <RoleBasedGuard roles={[roles.COMPANY_ADMIN]}><Outlet /></RoleBasedGuard>,
        children: [
          {path: 'reporting', element: <ReportingPage />},
          {path: 'company', element: <CompanyPage />},
          {path: 'settings', element: <SettingsPage />},
          {path: 'widgets', element: <WidgetsPage />},

          // Section gift-voucher
          {
            path: 'gift-voucher',
            children: [
              {index: true, element: <GiftVoucherCatalog />},
              {path: 'catalog', element: <GiftVoucherCatalog />},
              {path: 'catalog/create/:type', element: <GiftVouchersTypeCreate />},
              {path: 'catalog/edit/:type/:id', element: <GiftVouchersTypeEdit />},
              {path: 'manage', element: <GiftVouchersManage />},
              {path: 'transactions', element: <GiftVouchersTransactions />},
            ],
          },

          {
            path: 'customers',
            element: <FeatureGuard feature="customers"><Outlet /></FeatureGuard>,
            children: [
              {index: true, element: <CustomersListPage />},
            ],
          },

          {
            path: 'employees',
            element: <FeatureGuard feature="employees"><Outlet /></FeatureGuard>,
            children: [
              {index: true, element: <EmployeesListPage />},
            ],
          },

          // Section wellness avec FeatureGuard
          {
            path: 'wellness',
            element: <FeatureGuard feature="wellness"><Outlet /></FeatureGuard>,
            children: [
              {index: true, element: <LandingPage />},
              {
                path: 'catalog',
                children: [
                  {index: true, element: <CatalogPage />},
                  // Routes services dans catalog
                  {path: 'services/create', element: <ServiceCreatePage />},
                  {path: 'services/edit/:id', element: <ServiceEditPage />},
                  // Routes extras dans catalog
                  {path: 'extras/create', element: <ExtraCreatePage />},
                  {path: 'extras/edit/:id', element: <ExtraEditPage />},
                  // Routes produits dans catalog
                  {path: 'products/create', element: <ProductCreatePage />},
                  {path: 'products/edit/:id', element: <ProductEditPage />},
                ],
              },
              {path: 'spaces', element: <SpacesListPage />},
              {path: 'calendar', element: <CalendarPage />},
              {path: 'config', element: <WellnessConfigPage />},
              {path: 'bookings', element: <WellnessBookingsPage />},
              {path: 'bookings/create', element: <WellnessBookingCreatePage />},
              {path: 'bookings/edit/:id', element: <WellnessBookingEditPage />},
              {
                path: 'pos',
                children: [
                  {
                    index: true,
                    element: <Navigate to="sale" replace />,
                  },
                  {
                    path: 'sale',
                    element: <WellnessPOSPage />,
                  },
                  {
                    path: 'closing',
                    element: <WellnessPOSPage />,
                  },
                  {
                    path: 'reports',
                    element: <WellnessPOSPage />,
                  },
                  {
                    path: 'payment',
                    element: <WellnessPOSPaymentPage />,
                  },
                ],
              },
            ],
          },
          // Section restaurants avec FeatureGuard
          {
            path: 'restaurant',
            element: <FeatureGuard feature="restaurant"><Outlet /></FeatureGuard>,
            children: [
              {index: true, element: <LandingPage />},
              {path: 'seating', element: <RestaurantSeatingPage />},
              {path: 'services', element: <RestaurantServicesPage />},
              {path: 'reservations', element: <RestaurantReservationsPage />},

            ],
          },
        ],
      },

      // Routes publiques qui doivent rester accessibles
      {path: 'gift-voucher/details/:id', element: <GiftVouchersDetails />},
      {path: 'gift-voucher/search', element: <GiftVouchersSearch />},
    ],
  },
];
